import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Apple, PlayCircle, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "@/contexts/LanguageContext";
import { IOS_APP_URL, ANDROID_APP_URL, UNIVERSAL_DOWNLOAD_URL } from "@/lib/appStoreLinks";

// Public smart download URL — auto-redirects to the right app store
const SITE_URL = UNIVERSAL_DOWNLOAD_URL;

const BRAND_ICON = "/hood-icon.png";

export function InstallAppSection() {
  const { t } = useLang();

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-16 mb-10" data-testid="install-section">
      <Card className="p-6 sm:p-8 bg-gradient-to-bl from-primary/10 via-amber-50 to-rose-50 border-primary/20 overflow-hidden relative dark:from-emerald-950/40 dark:via-slate-900 dark:to-slate-900 dark:border-emerald-800/50">
        <div className="absolute -end-12 -top-12 size-40 rounded-full bg-primary/10" />
        <div className="absolute -start-10 -bottom-10 size-32 rounded-full bg-secondary/10" />
        <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-start">
          {/* Left: text + buttons */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <img src={BRAND_ICON} alt="هود" className="size-12 rounded-2xl shadow-lg object-cover" />
              <div>
                <h2 className="text-2xl sm:text-3xl font-black">{t("install.title")}</h2>
                <p className="text-xs text-muted-foreground">ثبّته على هاتفك بضغطة واحدة</p>
              </div>
            </div>
            <p className="text-muted-foreground mb-5 max-w-xl text-sm">
              {t("install.body")}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
              {/* Android — Play Store direct link */}
              <a
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-14 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white transition font-bold"
                data-testid="download-android-btn"
              >
                <PlayCircle className="size-5" />
                <div className="text-start">
                  <div className="text-[10px] leading-none opacity-90">حمّل من</div>
                  <div className="text-sm font-black leading-tight">Google Play</div>
                </div>
              </a>

              {/* iPhone — App Store link */}
              <a
                href={IOS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-14 rounded-md bg-black text-white hover:bg-black/90 transition font-bold"
                data-testid="ios-store-btn"
              >
                <Apple className="size-5" />
                <div className="text-start">
                  <div className="text-[10px] leading-none opacity-90">حمّل من</div>
                  <div className="text-sm font-black leading-tight">App Store</div>
                </div>
              </a>
            </div>
          </div>

          {/* Right column: QR code */}
          <QrCodePanel />
        </div>
      </Card>
    </section>
  );
}

function QrCodePanel() {
  const { t } = useLang();
  const [showPrintable, setShowPrintable] = useState(false);
  const downloadUrl = SITE_URL;

  const downloadPNG = () => {
    const svg = document.getElementById("khanaqin-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = 1024; c.height = 1024;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const a = document.createElement("a");
      a.download = "khanaqin-qr.png";
      a.href = c.toDataURL("image/png");
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <>
      <div className="hidden lg:block">
        <Card className="p-4 bg-white border-2 border-primary/30 shadow-lg dark:bg-slate-900 dark:border-emerald-700/50" data-testid="qr-panel">
          <div className="text-center mb-2 font-bold text-sm text-primary flex items-center justify-center gap-1.5 dark:text-emerald-400">
            <QrCode className="size-4" /> {t("install.scan")}
          </div>
          <div className="relative">
            <QRCodeSVG
              id="khanaqin-qr-svg"
              value={downloadUrl}
              size={180}
              level="H"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#16803c"
              imageSettings={{
                src: BRAND_ICON,
                height: 44,
                width: 44,
                excavate: true,
              }}
            />
          </div>
          <p className="text-[11px] text-center text-muted-foreground mt-2 leading-tight">
            افتح كاميرا الموبايل ووجّهها على الرمز
          </p>
          <div className="flex gap-1 mt-2">
            <Button size="sm" variant="outline" onClick={downloadPNG} className="flex-1 text-xs h-7 gap-1" data-testid="qr-download-png">
              <Download className="size-3" /> PNG
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowPrintable(true)} className="flex-1 text-xs h-7" data-testid="qr-printable">
              🖨️ للطباعة
            </Button>
          </div>
        </Card>
      </div>

      {/* Mobile: smaller inline QR */}
      <div className="lg:hidden mt-4 flex justify-center">
        <Card className="p-3 bg-white border-2 border-primary/30 inline-flex flex-col items-center dark:bg-slate-900 dark:border-emerald-700/50">
          <QRCodeSVG
            value={downloadUrl}
            size={140}
            level="M"
            bgColor="#ffffff"
            fgColor="#16803c"
          />
          <p className="text-[10px] text-muted-foreground mt-2 text-center">شارك مع أصدقائك</p>
        </Card>
      </div>

      {/* Printable poster modal */}
      {showPrintable && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPrintable(false)} data-testid="qr-print-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 print:shadow-none" onClick={(e) => e.stopPropagation()}>
            <div className="text-center" id="khanaqin-print">
              <img src={BRAND_ICON} alt="" className="size-16 mx-auto rounded-2xl shadow mb-2 object-cover" />
              <h3 className="text-2xl font-black mb-1">هود</h3>
              <p className="text-sm text-muted-foreground mb-4">دليل المحلات والأطباء والخدمات</p>
              <div className="inline-block p-3 bg-white border-4 border-primary rounded-xl shadow-xl">
                <QRCodeSVG
                  value={downloadUrl}
                  size={260}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#16803c"
                  imageSettings={{
                    src: BRAND_ICON,
                    height: 60,
                    width: 60,
                    excavate: true,
                  }}
                />
              </div>
              <p className="mt-4 font-bold text-base">📱 امسح الرمز لزيارة الموقع</p>
              <p className="text-xs text-muted-foreground mt-1 dir-ltr">{downloadUrl}</p>
            </div>
            <div className="flex gap-2 mt-5 print:hidden">
              <Button variant="outline" onClick={() => setShowPrintable(false)} className="flex-1">إغلاق</Button>
              <Button onClick={() => window.print()} className="flex-1 bg-primary gap-1">
                🖨️ طباعة
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Compact install panel intended for the drawer.
 * Shows small QR + Play Store button + App Store button.
 */
export function InstallAppCompact() {
  return (
    <div className="space-y-3" data-testid="drawer-install-app">
      {/* QR (points to the smart /download page) */}
      <div className="flex justify-center">
        <div className="bg-white p-2.5 rounded-2xl border-2 border-primary/30 shadow">
          <QRCodeSVG
            value={SITE_URL}
            size={140}
            level="H"
            bgColor="#ffffff"
            fgColor="#16803c"
            imageSettings={{
              src: BRAND_ICON,
              height: 32,
              width: 32,
              excavate: true,
            }}
          />
        </div>
      </div>
      <p className="text-[11px] text-center text-white/60">امسح الرمز — يفتح المتجر تلقائياً</p>

      {/* Store buttons */}
      <div className="grid grid-cols-2 gap-2">
        <a
          href={ANDROID_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="drawer-android-btn"
          className="h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition"
        >
          <PlayCircle className="size-4" />
          Android
        </a>
        <a
          href={IOS_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="drawer-ios-btn"
          className="h-12 rounded-2xl bg-black hover:bg-black/85 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition"
        >
          <Apple className="size-4" />
          App Store
        </a>
      </div>
    </div>
  );
}
